import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { useGoogleMaps } from "../../context/GoogleMapsContext";
import { useOrganization } from "../../context/OrganizationContext";
import { getCurrentDpaVersion } from "../../config/dpaConfig";
import { sanitizeInput, sanitizeDescription, sanitizeText } from "../../utils/sanitization";
import {
  listOrganizationBowlingCenters,
  unlinkOrganizationBowlingCenter,
} from "../../utility/organizationBowlingCenters";
import {
  clearOrganizationFirstSetup,
  getOrganizationFirstSetup,
  saveOrganizationFirstSetup,
} from "../../utility/organizationFirstSetup";
import AddCenterForm from "../TournamentLocations/AddCenterForm";
import SelectedCenters from "../TournamentLocations/SelectedCenters";
import TournamentInputs from "../CreateTournamentForm/TournamentInputs";
import HandicapInputs from "../CreateTournamentForm/HandicapInputs";
import InlineAlert from "../UI/DesignStandards/Notifications/InlineAlert";
import { PrimaryButton, ProgressSteps } from "../UI/DesignStandards";
import { emptyTournamentForm } from "../../utility/houseTournamentDefaults";
import { getAdminTabUrl } from "../../utility/adminNavigation";
import { handicapFormErrors } from "../../utility/handicapFieldValidation";
import { handicapValuesForSave } from "../../utility/tournamentScoringType";
import { requestTournamentIndexNow } from "../../utility/requestIndexNow";
import {
  getOrganizationTournamentCap,
  isTournamentCapError,
  tournamentAtCapMessage,
  tournamentCapUsageCopy,
} from "../../utility/tournamentCaps";

const SETUP_STEPS = ["Organization", "House", "Tournament"];

const parseRpcResult = (result) => {
  if (!result) return { success: false, error: "Failed to create organization" };
  if (typeof result === "string") {
    try {
      return JSON.parse(result);
    } catch {
      return { success: false, error: "Failed to create organization" };
    }
  }
  return result;
};

const OrganizationFirstSetup = ({ onStepChange }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isLoaded, loadError } = useGoogleMaps();
  const { refreshOrganizations } = useOrganization();
  const saved = getOrganizationFirstSetup();
  const cardRef = useRef(null);

  const [step, setStep] = useState(saved?.step || 1);
  const [organizationId, setOrganizationId] = useState(
    saved?.organizationId || null
  );
  const [orgName, setOrgName] = useState(saved?.orgName || "");
  const [orgNameAbbreviation, setOrgNameAbbreviation] = useState("");
  const [orgDescription, setOrgDescription] = useState("");
  const [dpaAccepted, setDpaAccepted] = useState(false);
  const [houseName, setHouseName] = useState(saved?.houseName || "");
  const [houses, setHouses] = useState([]);
  const [allCenters, setAllCenters] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [tournamentDetails, setTournamentDetails] = useState(emptyTournamentForm);
  const [tournamentErrors, setTournamentErrors] = useState({});

  const goToStep = (nextStep, extras = {}) => {
    setStep(nextStep);
    setErrorMessage("");
    onStepChange?.(nextStep);
    if (organizationId || extras.organizationId) {
      saveOrganizationFirstSetup({
        organizationId: extras.organizationId || organizationId,
        orgName: extras.orgName ?? orgName,
        houseName: extras.houseName ?? houseName,
        step: nextStep,
      });
    }
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const houseSummary = (centers) =>
    (centers || [])
      .map((row) => row.bowlingCenters?.centerName)
      .filter(Boolean)
      .join(", ");

  const loadHouses = useCallback(async () => {
    if (!organizationId) {
      setHouses([]);
      return [];
    }
    const centers = await listOrganizationBowlingCenters(organizationId);
    setHouses(centers);
    setHouseName(houseSummary(centers));
    return centers;
  }, [organizationId]);

  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  useEffect(() => {
    if (step < 2 || !organizationId) return;

    const loadCatalog = async () => {
      const { data, error } = await supabase
        .from("bowlingCenters")
        .select("*")
        .is("deletedAt", null);
      if (error) {
        console.error(error);
        return;
      }
      setAllCenters(data || []);
    };

    loadCatalog();
  }, [step, organizationId]);

  useEffect(() => {
    if (step !== 2 || !organizationId) return;
    loadHouses().catch((error) => console.error(error));
  }, [step, organizationId, loadHouses]);

  const handleCreateOrganization = async (event) => {
    event.preventDefault();
    if (!orgName.trim()) {
      setErrorMessage("Please enter an organization name");
      return;
    }
    if (!dpaAccepted) {
      setErrorMessage(
        "You must accept the Data Processing Agreement to create an organization"
      );
      return;
    }

    setIsSaving(true);
    setErrorMessage("");
    try {
      const { data: result, error: createError } = await supabase.rpc(
        "create_organization_with_owner_secure",
        {
          org_name: sanitizeInput(orgName),
          org_abbreviation: orgNameAbbreviation
            ? sanitizeInput(orgNameAbbreviation)
            : "",
          org_description: orgDescription
            ? sanitizeDescription(orgDescription)
            : null,
          dpa_version: getCurrentDpaVersion(),
        }
      );

      if (createError) throw createError;

      const payload = parseRpcResult(result);
      if (!payload.success) {
        throw new Error(payload.error || "Failed to create organization");
      }

      setOrganizationId(payload.organization_id);
      goToStep(2, {
        organizationId: payload.organization_id,
        orgName: orgName.trim(),
      });
    } catch (error) {
      console.error("Error creating organization:", error);
      setErrorMessage(
        error.message || "Failed to create organization. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleHouseAdded = async () => {
    try {
      const centers = await loadHouses();
      saveOrganizationFirstSetup({
        organizationId,
        orgName,
        houseName: houseSummary(centers),
        step: 2,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleHouseRemoved = async (bowlingCenterId) => {
    if (!organizationId || !bowlingCenterId) return;
    try {
      await unlinkOrganizationBowlingCenter({
        organizationId,
        bowlingCenterId,
      });
      await loadHouses();
    } catch (error) {
      console.error(error);
      setErrorMessage("Could not remove that bowling center.");
    }
  };

  const handleContinueFromHouses = () => {
    if (houses.length === 0) {
      setErrorMessage("Add at least one bowling center.");
      return;
    }
    goToStep(3, { houseName: houseSummary(houses) });
  };

  const handleCreateTournament = async (event) => {
    event.preventDefault();
    if (!organizationId) {
      setErrorMessage("Create the organization first.");
      return;
    }

    const errors = {
      name: tournamentDetails.name ? "" : "Tournament name is required.",
      startDate: tournamentDetails.startDate ? "" : "Start Date is required.",
      ...handicapFormErrors({
        scoringType: tournamentDetails.scoringType,
        handicapBase: tournamentDetails.handicapBase,
        handicapPercentage: tournamentDetails.handicapPercentage,
        handicapMax: tournamentDetails.handicapMax,
      }),
      payoutRatio:
        !tournamentDetails.payoutRatioNumerator ||
        !tournamentDetails.payoutRatioDenominator
          ? "Payout ratio is required"
          : Number(tournamentDetails.payoutRatioNumerator) <= 0 ||
              Number(tournamentDetails.payoutRatioDenominator) <= 0
            ? "Both numbers must be greater than 0"
            : "",
    };

    setTournamentErrors(errors);
    if (Object.values(errors).some(Boolean)) return;

    setIsSaving(true);
    setErrorMessage("");
    let cap;
    try {
      cap = await getOrganizationTournamentCap(organizationId);
      const capCopy = tournamentCapUsageCopy(cap);
      if (capCopy.atCap) {
        setErrorMessage(capCopy.message);
        return;
      }

      const { data: org, error: orgError } = await supabase
        .from("organizations")
        .select("defaultPayoutTableId")
        .eq("id", organizationId)
        .single();

      if (orgError) throw orgError;

      const { data, error } = await supabase
        .from("tournaments")
        .insert({
          name: sanitizeText(tournamentDetails.name),
          description: tournamentDetails.description
            ? sanitizeDescription(tournamentDetails.description)
            : null,
          ...handicapValuesForSave(tournamentDetails),
          startDate: tournamentDetails.startDate,
          organizationID: organizationId,
          payoutRatioNumerator: Number(tournamentDetails.payoutRatioNumerator),
          payoutRatioDenominator: Number(
            tournamentDetails.payoutRatioDenominator
          ),
          payoutTableId: org?.defaultPayoutTableId || null,
        })
        .select()
        .single();

      if (error) throw error;

      void requestTournamentIndexNow(data.id);
      clearOrganizationFirstSetup();
      await refreshOrganizations(organizationId);
      navigate(getAdminTabUrl(data.slug || data.id, "setup"));
    } catch (error) {
      console.error("Error creating tournament:", error);
      setErrorMessage(
        isTournamentCapError(error)
          ? tournamentAtCapMessage(cap?.tournamentLimit)
          : "Could not create the tournament. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div ref={cardRef} className="card-board">
      <div className="bg-brand-charcoal text-brand-cream px-6 py-4 rounded-t-lg">
        <h2 className="font-display font-bold text-xl text-brand-cream tracking-tight">
          Get started
        </h2>
        <p className="text-sm text-brand-mist mt-1">
          Create the organization, add a bowling center, then create a
          tournament.
        </p>
      </div>

      <div className="p-6 sm:p-8">
        <ProgressSteps
          steps={SETUP_STEPS}
          currentStep={step}
          className="mb-8 flex-wrap gap-y-3"
        />

        {errorMessage ? (
          <div className="mb-6 space-y-2">
            <InlineAlert
              type="error"
              title="There was a problem"
              message={errorMessage}
              onClose={() => setErrorMessage("")}
            />
            {/Open Plan/i.test(errorMessage) ? (
              <Link
                to="/organization/plan"
                className="inline-block text-sm underline text-brand-gold-ink"
              >
                Open Plan
              </Link>
            ) : null}
          </div>
        ) : null}

        {orgName && step > 1 ? (
          <p className="text-sm text-brand-muted mb-6">
            {orgName}
            {houseName ? ` · ${houseName}` : ""}
          </p>
        ) : null}

        {step === 1 ? (
          <form onSubmit={handleCreateOrganization} className="space-y-4">
            <div>
              <label
                htmlFor="orgName"
                className="block text-sm font-medium text-brand-charcoal mb-1"
              >
                Organization Name *
              </label>
              <input
                type="text"
                id="orgName"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                placeholder="e.g., Downtown Bowling League"
                required
              />
            </div>

            <div>
              <label
                htmlFor="orgNameAbbreviation"
                className="block text-sm font-medium text-brand-charcoal mb-1"
              >
                Short Name (Optional)
              </label>
              <input
                type="text"
                id="orgNameAbbreviation"
                value={orgNameAbbreviation}
                onChange={(e) => setOrgNameAbbreviation(e.target.value)}
                className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                placeholder="e.g., DBL"
              />
            </div>

            <div>
              <label
                htmlFor="orgDescription"
                className="block text-sm font-medium text-brand-charcoal mb-1"
              >
                Description (Optional)
              </label>
              <textarea
                id="orgDescription"
                value={orgDescription}
                onChange={(e) => setOrgDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                placeholder="Tell us about your organization..."
              />
            </div>

            <div className="border-t border-brand-charcoal/10 pt-4">
              <fieldset>
                <legend className="sr-only">
                  Data Processing Agreement Acceptance
                </legend>
                <label className="flex items-start cursor-pointer">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      checked={dpaAccepted}
                      onChange={(e) => setDpaAccepted(e.target.checked)}
                      className="focus:ring-brand-gold-ink h-4 w-4 text-brand-charcoal border-brand-charcoal/20 rounded"
                      required
                      aria-describedby="dpa-org-checkbox-description"
                      aria-required="true"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <span
                      id="dpa-org-checkbox-description"
                      className="text-brand-muted"
                    >
                      I agree to the{" "}
                      <a
                        href="/data-processing-agreement"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-gold-ink hover:text-brand-charcoal underline"
                        aria-label="Data Processing Agreement (opens in new window)"
                      >
                        Data Processing Agreement
                      </a>{" "}
                      and{" "}
                      <a
                        href="/data-retention-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-gold-ink hover:text-brand-charcoal underline"
                        aria-label="Data Retention Policy (opens in new window)"
                      >
                        Data Retention Policy
                      </a>
                      .
                    </span>
                  </div>
                </label>
              </fieldset>
            </div>

            <div className="flex justify-end pt-2">
              <PrimaryButton
                type="submit"
                loading={isSaving}
                disabled={isSaving || !dpaAccepted}
              >
                Continue
              </PrimaryButton>
            </div>
          </form>
        ) : null}

        {step === 2 ? (
          <div className="overflow-visible">
            <p className="text-sm text-brand-muted mb-4">
              Search and pick the house you bowl at. Add another if you use more
              than one.
            </p>
            {houses.length > 0 ? (
              <SelectedCenters
                selectedCenters={houses}
                onRemoveCenter={handleHouseRemoved}
                isLoggedIn={!!user}
                canManage
              />
            ) : null}
            {loadError ? (
              <InlineAlert
                type="error"
                title="There was a problem"
                message="Could not load Google Maps. Refresh the page and try again."
              />
            ) : !isLoaded ? (
              <div className="py-8" role="status" aria-label="Loading maps">
                <div className="space-y-3 animate-pulse">
                  <div className="h-10 bg-brand-charcoal/10 rounded" />
                  <div className="h-10 bg-brand-charcoal/10 rounded" />
                  <div className="h-10 bg-brand-charcoal/10 rounded w-2/3" />
                </div>
              </div>
            ) : (
              <AddCenterForm
                showForm
                hideToggle
                hideHeading
                embedded
                allCenters={allCenters}
                organizationId={organizationId}
                onCenterAdded={handleHouseAdded}
                isLoggedIn={!!user}
                canManage
              />
            )}
            <div className="flex justify-end pt-4">
              <PrimaryButton
                type="button"
                onClick={handleContinueFromHouses}
                disabled={houses.length === 0}
              >
                Continue
              </PrimaryButton>
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <form onSubmit={handleCreateTournament} className="space-y-6">
            <TournamentInputs
              tournamentDetails={tournamentDetails}
              setTournamentDetails={setTournamentDetails}
              organizations={[]}
              organizationalID={organizationId}
              setOrganizationalID={() => {}}
              errorMessages={tournamentErrors}
              hideOrganization
            />
            <div>
              <h2 className="heading-display text-lg mb-4">Scoring</h2>
              <HandicapInputs
                scoringName="first-setup-scoring"
                scoringType={tournamentDetails.scoringType}
                setScoringType={(value) =>
                  setTournamentDetails({
                    ...tournamentDetails,
                    scoringType: value,
                  })
                }
                handicapBase={tournamentDetails.handicapBase}
                setHandicapBase={(value) =>
                  setTournamentDetails({
                    ...tournamentDetails,
                    handicapBase: value,
                  })
                }
                handicapPercentage={tournamentDetails.handicapPercentage}
                setHandicapPercentage={(value) =>
                  setTournamentDetails({
                    ...tournamentDetails,
                    handicapPercentage: value,
                  })
                }
                handicapMax={tournamentDetails.handicapMax}
                setHandicapMax={(value) =>
                  setTournamentDetails({
                    ...tournamentDetails,
                    handicapMax: value,
                  })
                }
                errorMessages={tournamentErrors}
              />
            </div>
            <div className="flex justify-end pt-2">
              <PrimaryButton type="submit" loading={isSaving} disabled={isSaving}>
                Create tournament
              </PrimaryButton>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
};

export default OrganizationFirstSetup;
